import { useEffect, useRef, useState } from 'react';

/**
 * KHQR renderer.
 *
 * The QR is rendered locally rather than fetched from an image service: the
 * payload contains the merchant's account and the exact amount, so shipping it
 * to a third party on every checkout would leak transaction data, and the
 * customer would be staring at a spinner on Cambodian mobile data at the exact
 * moment they are deciding whether to trust us with money.
 */
export function QrCode({ payload, size = 260 }: { payload: string; size?: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Lazy-loaded so the catalog pages never pay for the QR encoder.
    void import('qrcode').then(async (module) => {
      if (cancelled || !canvasRef.current) return;
      try {
        await module.default.toCanvas(canvasRef.current, payload, {
          width: size,
          margin: 1,
          errorCorrectionLevel: 'M',
          color: { dark: '#000000', light: '#ffffff' },
        });
      } catch {
        if (!cancelled) setFailed(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [payload, size]);

  if (failed) {
    // Falling back to the raw payload still lets a customer pay: most bank
    // apps accept a pasted KHQR string.
    return (
      <code
        className="block w-full break-all rounded-[var(--radius-card)] p-3 text-[10px]"
        style={{ background: 'var(--color-surface)' }}
      >
        {payload}
      </code>
    );
  }

  return (
    <div className="flex justify-center">
      <canvas
        ref={canvasRef}
        width={size}
        height={size}
        className="rounded-[var(--radius-card)] bg-white p-2"
        style={{ width: size, height: size }}
      />
    </div>
  );
}
